// Resolves the home / facility elimination period for a shop-insurance hybrid
// quote, in descending order of trust:
//
//  1. `homeCareWaitingPeriod` / `facilityCareWaitingPeriod` — per-setting values
//     carried on the quote row. Authoritative: these are the only source that can
//     express an asymmetric product (NYL Asset Flex waives the home-care wait via
//     its care-coordination benefit but still requires 90 facility days).
//  2. `eliminationPeriod` — a single carrier-wide value, fanned out to both
//     settings. Correct for products that apply one period across care settings.
//  3. A carrier-name fallback for rows carrying neither.
//
// Tier 3 is a guess and is only reached when the row has no data at all. It is
// preserved verbatim from the two hardcoded copies this module replaces so that
// carriers without quote-level values keep their existing numbers; it is NOT
// carrier-verified. Populating tiers 1-2 for a product is what retires the guess
// for that product — do not add carriers here.
const CARRIERS_WITHOUT_HOME_CARE_WAIT = ['OneAmerica', 'Lincoln Financial'];
const CARRIERS_WITHOUT_FACILITY_CARE_WAIT = ['Lincoln Financial'];
const FALLBACK_WAITING_PERIOD_DAYS = 90;

// Non-day units are rejected rather than converted: reading "3 months" as 3 days
// would understate the client's out-of-pocket exposure by ~87 days, so an
// unrecognized unit falls through to the next tier instead of being guessed at.
const NON_DAY_UNIT = /month|year|week/i;

export type QuoteWaitingPeriodSource = {
  carrierName: string;
  homeCareWaitingPeriod?: string | null;
  facilityCareWaitingPeriod?: string | null;
  eliminationPeriod?: string | null;
};

// Quote columns are TEXT and arrive both bare ("0", "90") and unit-bearing
// ("90 days"). Zero is a legitimate waiting period, so absence must be signalled
// with null — never with a falsy number.
const parseWaitingPeriodDays = (value: string | null | undefined): number | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed || NON_DAY_UNIT.test(trimmed)) return null;

  const match = trimmed.match(/\d+(\.\d+)?/);
  if (!match) return null;

  return Math.round(parseFloat(match[0]));
};

const resolve = (
  perSettingValue: string | null | undefined,
  quote: QuoteWaitingPeriodSource,
  carrierFallbackDays: number,
): number => {
  const perSetting = parseWaitingPeriodDays(perSettingValue);
  if (perSetting !== null) return perSetting;

  const carrierWide = parseWaitingPeriodDays(quote.eliminationPeriod);
  if (carrierWide !== null) return carrierWide;

  return carrierFallbackDays;
};

export const resolveHomeCareWaitingPeriodDays = (quote: QuoteWaitingPeriodSource): number =>
  resolve(
    quote.homeCareWaitingPeriod,
    quote,
    CARRIERS_WITHOUT_HOME_CARE_WAIT.includes(quote.carrierName)
      ? 0
      : FALLBACK_WAITING_PERIOD_DAYS,
  );

export const resolveFacilityCareWaitingPeriodDays = (quote: QuoteWaitingPeriodSource): number =>
  resolve(
    quote.facilityCareWaitingPeriod,
    quote,
    CARRIERS_WITHOUT_FACILITY_CARE_WAIT.includes(quote.carrierName)
      ? 0
      : FALLBACK_WAITING_PERIOD_DAYS,
  );
